import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Clock, Package, Download } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { Plaatkop } from "@/components/Plaatkop";
import {
  WerkbonStatusBadge, WERKBON_STATUS_LABELS, WERKBON_SOORT_LABELS, DotLabel, PriorityBadge,
  formatNlDate, formatEuro,
} from "@/components/Badges";
import { formatUren, parseUren, UURTYPE_LABELS } from "@/lib/uren";
import { MoneyInput } from "@/components/MoneyInput";
import { parseAmount, amountToInput } from "@/lib/money";
import { MediaGallery } from "@/components/MediaGallery";
import { MediaUpload } from "@/components/MediaUpload";
import { ErrorState } from "@/components/ListState";
import { Code } from "@/components/Code";
import { WerkbonDocuments } from "@/components/WerkbonDocuments";
import { WerkbonDebiteur } from "@/components/WerkbonDebiteur";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// Round 26 §3: tabs go Logboek · Materiaal · Uren · Documenten · Media · Kosten.
// The Werk band is the body above the tabs — it is what the bon IS, not one
// view among six.

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="text-data uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-body text-foreground">{value}</div>
    </div>
  );
};

const GeldRow = ({ label, children, testid }) => (
  <div className="flex items-baseline justify-between gap-3 py-2" data-testid={testid}>
    <span className="text-body text-muted-foreground">{label}</span>
    <span className="font-mono tabular-nums text-body text-foreground">{children}</span>
  </div>
);

const EMPTY_UREN = { datum: "", uren: "", uurtype: "normaal", note: "", user_id: "" };

// Round 26 §3.1: system-written per-bon log. Human labels for the event codes.
const LOG_EVENT_LABELS = {
  created: "Aangemaakt",
  edited: "Bijgewerkt",
  issued: "Uitgegeven",
  gereed: "Gereed gemeld",
  cancelled: "Geannuleerd",
  factureerbaar: "Factureerbaar",
  material_issued: "Materiaal afgeboekt",
  material_cancelled: "Afgifte geannuleerd",
  material_returned: "Retour geboekt",
  material_return_cancelled: "Retour geannuleerd",
  verslag_added: "Verslag geschreven",
  verslag_edited: "Verslag aangepast",
  document_added: "Document toegevoegd",
  document_removed: "Document verwijderd",
  linked_fault: "Storing gekoppeld",
  unlinked_fault: "Storing ontkoppeld",
};

export default function WerkbonDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canWrite, hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwner = hasRole("owner");
  const [b, setB] = useState(null);
  const [uren, setUren] = useState([]);
  const [afgiften, setAfgiften] = useState([]);
  const [retouren, setRetouren] = useState([]);
  const [verslagen, setVerslagen] = useState([]);
  const [logboek, setLogboek] = useState([]);
  const [oorzaken, setOorzaken] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // §1.1 pattern: keep the tab across a refetch.
  const tab = searchParams.get("tab") || "logboek";
  const setTab = (v) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  // edit dialog
  const [editDlg, setEditDlg] = useState(false);
  const [editForm, setEditForm] = useState(null);
  // annuleer dialog
  const [cancelDlg, setCancelDlg] = useState(false);
  const [cancelReden, setCancelReden] = useState("");
  // factureerbaar-uit dialog
  const [factDlg, setFactDlg] = useState(false);
  const [factReden, setFactReden] = useState("");
  // uren dialog
  const [urenDlg, setUrenDlg] = useState(false);
  const [urenForm, setUrenForm] = useState(EMPTY_UREN);
  const [urenEditId, setUrenEditId] = useState(null);
  // materiaal dialog
  const [matDlg, setMatDlg] = useState(false);
  const [matRegels, setMatRegels] = useState([{ part_id: "", aantal: "", stukprijs: "" }]);
  const [matNote, setMatNote] = useState("");
  // verslag dialog (uitgevoerde werkzaamheden)
  const [verslagDlg, setVerslagDlg] = useState(false);
  const [verslagForm, setVerslagForm] = useState({ tekst: "", datum: "" });
  const [verslagEditId, setVerslagEditId] = useState(null);
  // retour dialog
  const [retourDlg, setRetourDlg] = useState(null); // afgifte
  const [retourForm, setRetourForm] = useState([]);
  const [retourNote, setRetourNote] = useState("");
  // unlink fault confirm
  const [unlinkDlg, setUnlinkDlg] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [bon, u, a, r, vs, lb, oz, asg, p] = await Promise.all([
        api.get(`/werkbonnen/${id}`),
        api.get(`/uren?werkbon_id=${id}`),
        api.get(`/afgiften?werkbon_id=${id}`),
        api.get(`/retouren?werkbon_id=${id}`).catch(() => ({ data: [] })),
        api.get(`/werkbonnen/${id}/verslagen`).catch(() => ({ data: [] })),
        api.get(`/werkbonnen/${id}/logboek`).catch(() => ({ data: [] })),
        api.get("/oorzaken").catch(() => ({ data: [] })),
        api.get("/users/assignees"),
        api.get("/parts").catch(() => ({ data: [] })),
      ]);
      setB(bon.data); setUren(u.data); setAfgiften(a.data); setRetouren(r.data);
      setVerslagen(vs.data); setLogboek(lb.data); setOorzaken(oz.data);
      setAssignees(asg.data); setParts(p.data);
    } catch (e) { setError(getErrorMessage(e, "Werkbon niet gevonden.")); }
    finally { if (!silent) setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const refreshBon = () => load({ silent: true });

  const advance = async (status) => {
    setBusy(true);
    try {
      await api.post(`/werkbonnen/${id}/status`, { status });
      toast.success(status === "gereed" ? "Werkbon gereed gemeld" : "Werkbon uitgegeven");
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Status wijzigen mislukt.")); }
    finally { setBusy(false); }
  };

  const cancelBon = async () => {
    setBusy(true);
    try {
      await api.post(`/werkbonnen/${id}/status`, { status: "geannuleerd", reden: cancelReden });
      toast.success("Werkbon geannuleerd — de geboekte uren blijven staan en blijven zichtbaar onder Uitgezet.");
      setCancelDlg(false); setCancelReden("");
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Annuleren mislukt.")); }
    finally { setBusy(false); }
  };

  const setFactureerbaar = async (value, reden) => {
    setBusy(true);
    try {
      await api.post(`/werkbonnen/${id}/factureerbaar`, { factureerbaar: value, reden });
      toast.success(value ? "Werkbon staat weer op factureerbaar" : "Werkbon uitgezet — vastgelegd met naam en reden");
      setFactDlg(false); setFactReden("");
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Wijzigen mislukt.")); }
    finally { setBusy(false); }
  };

  const openEdit = () => {
    setEditForm({
      omschrijving: b.omschrijving || "",
      soort: b.soort,
      assigned_to: b.assigned_to || "__none__",
      streefdatum: b.streefdatum || "",
      geplande_start: b.geplande_start || "",
      geplande_eind: b.geplande_eind || "",
      geplande_uren: b.geplande_uren == null ? "" : String(b.geplande_uren).replace(".", ","),
      gereeddatum: b.gereeddatum || "",
      volledige_dag: Boolean(b.volledige_dag),
      werkbeschrijving: b.werkbeschrijving || "",
      prioriteit: b.prioriteit || "P3",
      oorzaak_id: b.oorzaak_id || "__none__",
      performed_by: b.performed_by || "td",
      external_party: b.external_party || "",
      cost_external_ex_vat: amountToInput(b.cost_external_ex_vat),
      notes: b.notes || "",
    });
    setEditDlg(true);
  };

  const saveEdit = async () => {
    const { value: extern, error: extErr } = parseAmount(editForm.cost_external_ex_vat);
    if (extErr) { toast.error(extErr); return; }
    let plannedHours = null;
    if (editForm.geplande_uren !== "") {
      const n = Number(String(editForm.geplande_uren).replace(",", "."));
      if (Number.isNaN(n)) { toast.error("Geplande uren onleesbaar."); return; }
      plannedHours = n;
    }
    setBusy(true);
    try {
      await api.put(`/werkbonnen/${id}`, {
        omschrijving: editForm.omschrijving,
        soort: editForm.soort,
        assigned_to: editForm.assigned_to === "__none__" ? null : editForm.assigned_to,
        streefdatum: editForm.streefdatum || null,
        geplande_start: editForm.geplande_start || null,
        geplande_eind: editForm.geplande_eind || null,
        geplande_uren: plannedHours,
        gereeddatum: editForm.gereeddatum || null,
        volledige_dag: editForm.volledige_dag,
        werkbeschrijving: editForm.werkbeschrijving,
        prioriteit: editForm.prioriteit,
        oorzaak_id: editForm.oorzaak_id === "__none__" ? null : editForm.oorzaak_id,
        performed_by: editForm.performed_by,
        external_party: editForm.performed_by === "extern" ? (editForm.external_party || null) : null,
        cost_external_ex_vat: extern,
        notes: editForm.notes,
      });
      toast.success("Werkbon bijgewerkt");
      setEditDlg(false);
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setBusy(false); }
  };

  const openUren = (line) => {
    if (line) {
      setUrenEditId(line.id);
      setUrenForm({ datum: line.datum, uren: formatUren(line.uren), uurtype: line.uurtype, note: line.note || "", user_id: line.user_id });
    } else {
      setUrenEditId(null);
      setUrenForm({ ...EMPTY_UREN, datum: new Date().toISOString().slice(0, 10), user_id: user?.id || "" });
    }
    setUrenDlg(true);
  };

  const saveUren = async () => {
    const { value, error: uErr } = parseUren(urenForm.uren);
    if (uErr || value === null) { toast.error(uErr || "Vul het aantal uren in."); return; }
    setBusy(true);
    try {
      if (urenEditId) {
        await api.put(`/uren/${urenEditId}`, { datum: urenForm.datum, uren: value, uurtype: urenForm.uurtype, note: urenForm.note || null });
      } else {
        await api.post("/uren", { werkbon_id: id, datum: urenForm.datum, uren: value, uurtype: urenForm.uurtype, note: urenForm.note || null, user_id: urenForm.user_id || null });
      }
      toast.success(`Geboekt: ${formatUren(value)} uur`);
      setUrenDlg(false);
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, `Niet opgeslagen: "${formatUren(value)} uur"`)); }
    finally { setBusy(false); }
  };

  const deleteUren = async () => {
    setBusy(true);
    try {
      await api.delete(`/uren/${urenEditId}`);
      toast.success("Urenregel verwijderd");
      setUrenDlg(false);
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); }
    finally { setBusy(false); }
  };

  const saveMateriaal = async () => {
    setBusy(true);
    try {
      const regels = matRegels.map((r) => ({
        part_id: r.part_id || null,
        aantal: r.aantal === "" ? null : Number(String(r.aantal).replace(",", ".")),
        stukprijs_ex_btw: r.stukprijs === "" ? null : parseAmount(r.stukprijs).value,
      }));
      const { data } = await api.post("/afgiften", { werkbon_id: id, regels, note: matNote || null });
      toast.success(`Afgifte ${data.afgifte_number} geboekt — voorraad bijgewerkt`);
      (data.low_stock_warnings || []).forEach((w) =>
        toast.warning(`${w.name}: voorraad ${w.current_stock}, minimum ${w.min_stock}`));
      setMatDlg(false); setMatRegels([{ part_id: "", aantal: "", stukprijs: "" }]); setMatNote("");
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Afgifte niet geboekt.")); }
    finally { setBusy(false); }
  };

  const cancelAfgifte = async (afgifteId) => {
    setBusy(true);
    try {
      await api.post(`/afgiften/${afgifteId}/annuleer`);
      toast.success("Afgifte geannuleerd — de voorraad is teruggezet");
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Annuleren mislukt.")); }
    finally { setBusy(false); }
  };

  const openRetour = (afgifte) => {
    const already = {};
    for (const rt of retouren) {
      if (rt.status !== "geboekt" || rt.afgifte_id !== afgifte.id) continue;
      for (const r of rt.regels || []) already[r.part_id] = (already[r.part_id] || 0) + (r.aantal || 0);
    }
    const rows = (afgifte.regels || []).map((r) => ({
      part_id: r.part_id, part_naam: r.part_naam,
      afgegeven: r.aantal,
      al_retour: already[r.part_id] || 0,
      rest: (r.aantal || 0) - (already[r.part_id] || 0),
      aantal: "",
    }));
    setRetourForm(rows);
    setRetourNote("");
    setRetourDlg(afgifte);
  };

  const saveRetour = async () => {
    const regels = retourForm
      .filter((r) => String(r.aantal).trim() !== "")
      .map((r) => ({ part_id: r.part_id, aantal: Number(String(r.aantal).replace(",", ".")) }));
    if (regels.length === 0) { toast.error("Vul minstens één aantal in."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/retouren", {
        afgifte_id: retourDlg.id, regels, note: retourNote || null,
      });
      toast.success(`Retour ${data.retour_number} geboekt — voorraad is verhoogd`);
      setRetourDlg(null);
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Retour mislukt.")); }
    finally { setBusy(false); }
  };

  const cancelRetour = async (retourId) => {
    setBusy(true);
    try {
      await api.post(`/retouren/${retourId}/annuleer`);
      toast.success("Retour geannuleerd — voorraad opnieuw omlaag");
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Annuleren mislukt.")); }
    finally { setBusy(false); }
  };

  const openVerslag = (v) => {
    if (v) {
      setVerslagEditId(v.id);
      setVerslagForm({ tekst: v.tekst, datum: v.datum });
    } else {
      setVerslagEditId(null);
      setVerslagForm({ tekst: "", datum: new Date().toISOString().slice(0, 10) });
    }
    setVerslagDlg(true);
  };

  const saveVerslag = async () => {
    if (!verslagForm.tekst.trim()) { toast.error("Schrijf iets."); return; }
    setBusy(true);
    try {
      if (verslagEditId) {
        await api.put(`/werkbonnen/${id}/verslagen/${verslagEditId}`, {
          tekst: verslagForm.tekst, datum: verslagForm.datum || null,
        });
      } else {
        await api.post(`/werkbonnen/${id}/verslagen`, {
          tekst: verslagForm.tekst, datum: verslagForm.datum || null,
        });
      }
      toast.success("Verslag opgeslagen");
      setVerslagDlg(false);
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setBusy(false); }
  };

  const unlinkFault = async () => {
    setBusy(true);
    try {
      await api.post(`/werkbonnen/${id}/unlink-fault`);
      toast.success("Storing losgekoppeld");
      setUnlinkDlg(false);
      await refreshBon();
    } catch (e) { toast.error(getErrorMessage(e, "Ontkoppelen mislukt.")); }
    finally { setBusy(false); }
  };

  const partById = useMemo(() => Object.fromEntries(parts.map((p) => [p.id, p])), [parts]);
  const urenTotaal = b?.uren_totaal || 0;

  // Per-part totals for §5-shaped material table (Afgeschreven · Geretourneerd · Totaal).
  const materiaalPerPart = useMemo(() => {
    const per = {};
    for (const a of afgiften || []) {
      if (a.status !== "geboekt") continue;
      for (const r of a.regels || []) {
        const key = r.part_id;
        per[key] = per[key] || { part_id: key, naam: r.part_naam, afg: 0, ret: 0 };
        per[key].afg += r.aantal || 0;
      }
    }
    for (const rt of retouren || []) {
      if (rt.status !== "geboekt") continue;
      for (const r of rt.regels || []) {
        const key = r.part_id;
        per[key] = per[key] || { part_id: key, naam: r.part_naam, afg: 0, ret: 0 };
        per[key].ret += r.aantal || 0;
      }
    }
    return Object.values(per);
  }, [afgiften, retouren]);

  if (loading) {
    return (
      <div className="page-body space-y-4" data-testid="werkbon-loading">
        <Skeleton className="h-9 w-48" />
        <Card className="p-5"><Skeleton className="h-40 w-full" /></Card>
      </div>
    );
  }
  if (error || !b) {
    return (
      <div className="page-body space-y-4">
        <Button variant="ghost" onClick={() => navigate("/werkbonnen")}><ArrowLeft size={18} className="mr-2" /> Terug</Button>
        <ErrorState testid="werkbon-load-error" message={error || "Werkbon niet gevonden."} onRetry={load} />
      </div>
    );
  }

  const isCancelled = b.status === "geannuleerd";
  const writable = canWrite() && !isCancelled;

  const openPdf = async () => {
    try {
      const resp = await api.get(`/werkbonnen/${id}/pdf`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) { toast.error(getErrorMessage(e, "PDF openen mislukt.")); }
  };

  return (
    <div className="page">
      <Plaatkop
        code={b.werkbon_number}
        srTitle={b.omschrijving || "Werkbon"}
        srTestid="werkbon-detail-title"
        subtitle={[b.location_name, b.ruimte_name, b.spoor, b.asset_code].filter(Boolean).join(" · ")}
        collapsibleFields
        backSlot={
          <Button variant="ghost" size="icon" data-testid="werkbon-back-button" className="-ml-2 text-white hover:bg-plaat-lijn hover:text-white" onClick={() => navigate("/werkbonnen")}>
            <ArrowLeft size={18} />
          </Button>
        }
        fields={[
          { label: "Machine", value: b.asset_label },
          { label: "Asset", value: b.asset_code, mono: true },
          { label: "Locatie", value: b.location_name },
          { label: "Soort", value: WERKBON_SOORT_LABELS[b.soort] },
          { label: "Streefdatum", value: b.streefdatum ? formatNlDate(b.streefdatum) : null, mono: true },
          { label: "Gereed", value: b.closed_at ? formatNlDate(b.closed_at) : null, mono: true },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" data-testid="werkbon-pdf-button"
              className="h-9 border border-plaat-lijn text-white hover:bg-plaat-lijn"
              onClick={openPdf}>
              <Download size={16} className="mr-1.5" /> PDF
            </Button>
            {writable && (
              <Button variant="secondary" size="sm" data-testid="werkbon-edit-button" onClick={openEdit}>
                <Pencil size={18} className="mr-1.5" /> Bewerken
              </Button>
            )}
          </div>
        }
      />

      <div className="page-body space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <WerkbonStatusBadge status={b.status} />
          {b.prioriteit && <PriorityBadge priority={b.prioriteit} />}
          <DotLabel tone="neutraal" word testid="werkbon-soort-label">{WERKBON_SOORT_LABELS[b.soort]}</DotLabel>
          {b.oorzaak_name && <DotLabel tone="neutraal" word testid="werkbon-oorzaak-label">{b.oorzaak_name}</DotLabel>}
          {!b.factureerbaar && <DotLabel tone="neutraal" word testid="werkbon-nietfactureerbaar-label">Niet factureerbaar</DotLabel>}
        </div>

        {writable && (
          <div className="flex flex-wrap gap-2">
            {b.status === "voorbereid" && (
              <Button size="sm" data-testid="werkbon-advance-uitgegeven" onClick={() => advance("uitgegeven")} disabled={busy}>Uitgeven</Button>
            )}
            {b.status === "uitgegeven" && (
              <Button size="sm" data-testid="werkbon-advance-gereed" onClick={() => advance("gereed")} disabled={busy}>Gereed melden</Button>
            )}
            {b.status !== "gereed" && (
              <Button size="sm" variant="outline" data-testid="werkbon-annuleer-button" onClick={() => setCancelDlg(true)} disabled={busy}>Annuleren</Button>
            )}
          </div>
        )}
        {isCancelled && b.annulering && (
          <p className="text-body text-muted-foreground" data-testid="werkbon-annulering-info">
            Geannuleerd door {b.annulering.door_naam || "onbekend"} op {formatNlDate((b.annulering.at || "").slice(0, 10))}
            {b.annulering.reden ? ` — "${b.annulering.reden}"` : ""}. De geboekte uren blijven staan.
          </p>
        )}

        {/* Werk band — the body above the tabs (§3: it is what the bon IS). */}
        <Card className="p-5" data-testid="werkbon-werk-band">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Omschrijving" value={b.omschrijving} />
            <Field label="Toegewezen" value={b.assignee_name} />
            <Field label="Uitgevoerd door" value={b.performed_by === "extern" ? `Extern${b.external_party ? ` — ${b.external_party}` : ""}` : "TD"} />
            <Field label="Geplande start" value={b.geplande_start ? formatNlDate(b.geplande_start) : null} />
            <Field label="Geplande eind" value={b.geplande_eind ? formatNlDate(b.geplande_eind) : null} />
            <Field label="Geplande uren" value={b.geplande_uren != null ? `${formatUren(b.geplande_uren)}${urenTotaal > 0 ? ` · geboekt ${formatUren(urenTotaal)}` : ""}` : null} />
            <Field label="Gereeddatum" value={b.gereeddatum ? formatNlDate(b.gereeddatum) : null} />
            <Field label="Notities" value={b.notes} />
          </div>
          {b.werkbeschrijving && (
            <div className="mt-4" data-testid="werkbon-werkbeschrijving">
              <div className="text-data uppercase tracking-wide text-muted-foreground">Werkbeschrijving</div>
              <p className="mt-1 whitespace-pre-wrap text-body text-foreground">{b.werkbeschrijving}</p>
            </div>
          )}
          {b.fault_id && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button type="button" className="text-body font-medium text-primary underline" data-testid="werkbon-fault-link" onClick={() => navigate(`/storingen/${b.fault_id}`)}>
                Gekoppelde storing bekijken
              </button>
              {writable && (
                <Button size="sm" variant="ghost" data-testid="werkbon-unlink-fault" onClick={() => setUnlinkDlg(true)}>
                  Loskoppelen
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Uitgevoerde werkzaamheden — its own record (§3.2). */}
        <Card className="p-5" data-testid="werkbon-verslagen">
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-body font-semibold text-foreground">Uitgevoerde werkzaamheden</h2>
              <p className="text-data text-muted-foreground">Per persoon, per dag. De schrijver is niet altijd degene wiens uren op de bon staan.</p>
            </div>
            {writable && (
              <Button size="sm" data-testid="verslag-toevoegen-button" onClick={() => openVerslag(null)}>
                <Plus size={16} className="mr-1.5" /> Verslag schrijven
              </Button>
            )}
          </div>
          {verslagen.length === 0 ? (
            <p className="text-body text-muted-foreground" data-testid="verslagen-empty">Nog geen verslag geschreven.</p>
          ) : (
            <ol className="space-y-3" data-testid="verslagen-list">
              {verslagen.map((v) => {
                const editable = writable && (isOwner || v.user_id === user?.id);
                return (
                  <li key={v.id} data-testid={`verslag-${v.id}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="flex items-baseline gap-2">
                        <Code size="small">{v.user_name || "-"}</Code>
                        <Code size="small">{formatNlDate(v.datum)}</Code>
                      </div>
                      {editable && (
                        <Button size="sm" variant="ghost" data-testid={`verslag-edit-${v.id}`} onClick={() => openVerslag(v)}>
                          <Pencil size={14} />
                        </Button>
                      )}
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-body text-foreground">{v.tekst}</p>
                  </li>
                );
              })}
            </ol>
          )}
        </Card>

        {/* Controlled Tabs, restored. The prior fork switched to
            `defaultValue` after finding zero dialogs measured on tab routes.
            That was the WRONG conclusion: the dialog audit's guard treated
            "URL now has `?tab=uren`" (set by `onValueChange` → setSearchParams)
            as "the trigger navigated away" and skipped the measurement.
            Fix landed in `/app/_qa_dialog_audit.py` (strip `?` and `#` before
            comparing), controlled Tabs stay — they support later URL-driven
            navigation to a specific tab, which `defaultValue` does not.
        */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="tabstrip">
            <TabsTrigger value="logboek" data-testid="werkbon-tab-logboek">Logboek</TabsTrigger>
            <TabsTrigger value="materiaal" data-testid="werkbon-tab-materiaal">Materiaal</TabsTrigger>
            <TabsTrigger value="uren" data-testid="werkbon-tab-uren">Uren{urenTotaal > 0 ? ` (${formatUren(urenTotaal)})` : ""}</TabsTrigger>
            <TabsTrigger value="documenten" data-testid="werkbon-tab-documenten">Documenten</TabsTrigger>
            <TabsTrigger value="media" data-testid="werkbon-tab-media">Media</TabsTrigger>
            <TabsTrigger value="kosten" data-testid="werkbon-tab-kosten">Kosten</TabsTrigger>
          </TabsList>

          <TabsContent value="logboek" className="mt-4">
            <Card className="p-5" data-testid="werkbon-logboek">
              <h2 className="mb-3 text-body font-semibold text-foreground">Logboek</h2>
              <p className="mb-3 text-data text-muted-foreground">Wat er met deze bon gebeurd is — geschreven door het systeem, nooit getypt. Niet aan te passen, niet te verwijderen.</p>
              {logboek.length === 0 ? (
                <p className="text-body text-muted-foreground" data-testid="logboek-empty">Nog geen gebeurtenissen.</p>
              ) : (
                <ol className="space-y-2" data-testid="logboek-list">
                  {logboek.map((e) => (
                    <li key={e.id} className="flex flex-col gap-0.5 border-l-2 border-lijn-zacht pl-3" data-testid={`logboek-entry-${e.id}`}>
                      <div className="flex items-baseline gap-2 text-data text-muted-foreground">
                        <Code size="small">{formatNlDate((e.at || "").slice(0, 10))}</Code>
                        <span>{(e.at || "").slice(11, 16)}</span>
                        <span>·</span>
                        <span>{e.actor_name || "systeem"}</span>
                        <span>·</span>
                        <span className="uppercase tracking-wide">{LOG_EVENT_LABELS[e.event] || e.event}</span>
                      </div>
                      <div className="text-body text-foreground">{e.tekst}</div>
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="materiaal" className="mt-4 space-y-4">
            <Card className="p-5">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-body font-semibold text-foreground">Materiaal — afgeschreven, geretourneerd, totaal</h2>
                {writable && (
                  <Button size="sm" data-testid="werkbon-materiaal-button" onClick={() => setMatDlg(true)}>
                    <Package size={18} className="mr-1.5" /> Materiaal afboeken
                  </Button>
                )}
              </div>
              {materiaalPerPart.length === 0 ? (
                <p className="py-4 text-body text-muted-foreground" data-testid="werkbon-materiaal-empty">Nog geen materiaal afgeboekt.</p>
              ) : (
                <div className="mb-4 border border-lijn-zacht" data-testid="werkbon-materiaal-totalen">
                  <table className="w-full text-body">
                    <thead className="bg-muted/50 text-data uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">Onderdeel</th>
                        <th className="px-3 py-2 text-right">Afgeschreven</th>
                        <th className="px-3 py-2 text-right">Geretourneerd</th>
                        <th className="px-3 py-2 text-right">Totaal</th>
                      </tr>
                    </thead>
                    <tbody className="font-mono tabular-nums">
                      {materiaalPerPart.map((r) => (
                        <tr key={r.part_id} data-testid={`materiaal-totaal-${r.part_id}`}>
                          <td className="px-3 py-2 font-sans text-foreground">{r.naam}</td>
                          <td className="px-3 py-2 text-right">{formatUren(r.afg)}</td>
                          <td className="px-3 py-2 text-right">{formatUren(r.ret)}</td>
                          <td className="px-3 py-2 text-right font-semibold">{formatUren(r.afg - r.ret)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {afgiften.length > 0 && (
                <div className="space-y-3" data-testid="werkbon-afgiften-list">
                  {afgiften.map((a) => {
                    const bonRetouren = retouren.filter((r) => r.afgifte_id === a.id);
                    return (
                      <div key={a.id} className={`border border-lijn-zacht p-3 ${a.status === "geannuleerd" ? "opacity-60" : ""}`} data-testid={`afgifte-${a.id}`}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Code>{a.afgifte_number}</Code>
                            {a.status === "geannuleerd" && <DotLabel tone="neutraal" word>Geannuleerd</DotLabel>}
                          </div>
                          <div className="flex items-center gap-2">
                            <Code size="small">{formatNlDate(a.datum)}</Code>
                            {canWrite() && a.status === "geboekt" && (
                              <>
                                <Button size="sm" variant="outline" data-testid={`afgifte-retour-${a.id}`} onClick={() => openRetour(a)} disabled={busy}>Retour</Button>
                                <Button size="sm" variant="outline" data-testid={`afgifte-annuleer-${a.id}`} onClick={() => cancelAfgifte(a.id)} disabled={busy}>Annuleren</Button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 space-y-1">
                          {(a.regels || []).map((r, i) => (
                            <div key={i} className="flex items-baseline justify-between gap-2 text-body">
                              <span className="text-foreground">{r.part_naam} × {formatUren(r.aantal)}</span>
                              {r.stukprijs_ex_btw != null && (
                                <span className="font-mono tabular-nums text-data text-muted-foreground">{formatEuro(r.aantal * r.stukprijs_ex_btw)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                        {a.afgegeven_door_naam && <div className="mt-1 text-data text-muted-foreground">Afgegeven door {a.afgegeven_door_naam}</div>}
                        {a.note && <div className="text-data text-muted-foreground">{a.note}</div>}
                        {bonRetouren.length > 0 && (
                          <div className="mt-2 space-y-1 border-t border-lijn-zacht pt-2" data-testid={`afgifte-retouren-${a.id}`}>
                            {bonRetouren.map((rt) => (
                              <div key={rt.id} className={`flex flex-wrap items-baseline justify-between gap-2 ${rt.status === "geannuleerd" ? "opacity-60" : ""}`} data-testid={`retour-${rt.id}`}>
                                <div className="flex items-baseline gap-2 text-data">
                                  <Code size="small">{rt.retour_number}</Code>
                                  <Code size="small">{formatNlDate(rt.datum)}</Code>
                                  <span className="text-muted-foreground">
                                    {(rt.regels || []).map((r) => `${r.part_naam} × ${formatUren(r.aantal)}`).join(", ")}
                                  </span>
                                  {rt.status === "geannuleerd" && <DotLabel tone="neutraal" word>Geannuleerd</DotLabel>}
                                </div>
                                {canWrite() && rt.status === "geboekt" && (
                                  <Button size="sm" variant="ghost" data-testid={`retour-annuleer-${rt.id}`} onClick={() => cancelRetour(rt.id)} disabled={busy}>Annuleren</Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="uren" className="mt-4 space-y-4">
            <Card className="p-5">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-body font-semibold text-foreground">Uren op deze bon{urenTotaal > 0 ? ` — ${formatUren(urenTotaal)} uur` : ""}</h2>
                {canWrite() && (
                  <Button size="sm" data-testid="werkbon-uren-boeken-button" onClick={() => openUren(null)}>
                    <Clock size={18} className="mr-1.5" /> Uren boeken
                  </Button>
                )}
              </div>
              {uren.length === 0 ? (
                <p className="py-4 text-body text-muted-foreground" data-testid="werkbon-uren-empty">Nog geen uren geboekt.</p>
              ) : (
                <div className="divide-y divide-border" data-testid="werkbon-uren-list">
                  {uren.map((l) => (
                    <button key={l.id} type="button" data-testid={`werkbon-urenregel-${l.id}`} className="block w-full py-2.5 text-left transition-colors hover:bg-accent" onClick={() => canWrite() && openUren(l)}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-baseline gap-3">
                          <span className="font-mono tabular-nums text-body font-medium text-foreground">{formatUren(l.uren)} uur</span>
                          <span className="text-data text-muted-foreground">{UURTYPE_LABELS[l.uurtype]}</span>
                        </div>
                        <div className="flex items-center gap-3 text-data text-muted-foreground">
                          <span>{l.user_name}</span>
                          <Code size="small">{formatNlDate(l.datum)}</Code>
                        </div>
                      </div>
                      {l.note && <div className="mt-0.5 text-data text-muted-foreground">{l.note}</div>}
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="documenten" className="mt-4">
            <WerkbonDocuments werkbon={b} onChange={refreshBon} />
          </TabsContent>

          <TabsContent value="media" className="mt-4">
            <Card className="p-5" data-testid="werkbon-media-tab">
              <h2 className="mb-3 text-body font-semibold text-foreground">Media</h2>
              {(b.media || []).length > 0 ? <MediaGallery media={b.media} /> : <p className="text-body text-muted-foreground">Nog geen foto's.</p>}
              {writable && (
                <div className="mt-3" data-testid="werkbon-media-add">
                  <MediaUpload value={b.media || []} onChange={async (m) => { try { await api.put(`/werkbonnen/${id}`, { media: m }); await refreshBon(); } catch (e) { toast.error(getErrorMessage(e, "Media opslaan mislukt.")); } }} apiClient={api} uploadPath="/storage/upload" confirmRemove />
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="kosten" className="mt-4 space-y-4">
            {b.factuur_nummer && (
              <Card className="p-4 border-l-2 border-l-borstel" data-testid="werkbon-factuur-banner">
                <div className="text-body font-semibold text-foreground">Op factuur {b.factuur_nummer}</div>
                <p className="text-data text-muted-foreground">Deze werkbon staat op een uitgegeven factuur en is bevroren: geen nieuwe uren, materiaal of factureerbaar-wijziging. Materiaal dat terugkomt boek je met een retour; dat komt op de eerstvolgende creditnota.</p>
                {canWrite() && (
                  <Button variant="outline" size="sm" className="mt-2" data-testid="werkbon-naar-factuur" onClick={() => navigate(`/facturen/${b.factuur_id}`)}>Naar factuur {b.factuur_nummer}</Button>
                )}
              </Card>
            )}
            <Card className="p-5">
              <h2 className="mb-2 text-body font-semibold text-foreground">Kosten (ex btw)</h2>
              <div className="divide-y divide-border">
                {urenTotaal > 0 && (
                  <GeldRow label={`Uren (${formatUren(urenTotaal)})`} testid="geld-uren">
                    {b.tarief_ontbreekt
                      ? <span className="font-sans text-muted-foreground" data-testid="geld-tarief-ontbreekt">tarief nog niet ingesteld</span>
                      : formatEuro(b.bedrag_uren || 0)}
                  </GeldRow>
                )}
                {b.bedrag_materiaal > 0 && <GeldRow label="Materiaal" testid="geld-materiaal">{formatEuro(b.bedrag_materiaal)}</GeldRow>}
                {b.cost_external_ex_vat != null && b.cost_external_ex_vat !== 0 && (
                  <GeldRow label={`Extern${b.external_party ? ` — ${b.external_party}` : ""}`} testid="geld-extern">{formatEuro(b.cost_external_ex_vat)}</GeldRow>
                )}
                {b.legacy_bedrag_arbeid != null && (
                  <GeldRow label="Arbeid (overgenomen uit reparatie)" testid="geld-legacy-arbeid">{formatEuro(b.legacy_bedrag_arbeid)}</GeldRow>
                )}
                {b.legacy_bedrag_materiaal != null && (
                  <GeldRow label="Materiaal (overgenomen uit reparatie)" testid="geld-legacy-materiaal">{formatEuro(b.legacy_bedrag_materiaal)}</GeldRow>
                )}
                {b.bedrag_totaal != null && b.bedrag_totaal !== 0 && (
                  <div className="flex items-baseline justify-between gap-3 py-2" data-testid="geld-totaal">
                    <span className="text-body font-semibold text-foreground">Totaal</span>
                    <span className="font-mono tabular-nums text-kop text-foreground">{formatEuro(b.bedrag_totaal)}</span>
                  </div>
                )}
                {urenTotaal === 0 && b.bedrag_materiaal === 0 && (b.bedrag_totaal == null || b.bedrag_totaal === 0) && (
                  <p className="py-3 text-body text-muted-foreground" data-testid="geld-leeg">Nog geen uren of materiaal op deze bon.</p>
                )}
              </div>
            </Card>

            <WerkbonDebiteur bon={b} canWrite={canWrite()} onSaved={refreshBon} />

            <Card className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-body font-semibold text-foreground">Factureerbaar</div>
                  <p className="text-data text-muted-foreground">Aan, tenzij iemand bewust anders zegt — met naam en reden.</p>
                </div>
                <Switch
                  data-testid="werkbon-factureerbaar-switch"
                  checked={Boolean(b.factureerbaar)}
                  disabled={!canWrite() || busy || Boolean(b.factuur_nummer)}
                  onCheckedChange={(v) => { if (v) { setFactureerbaar(true, null); } else { setFactReden(""); setFactDlg(true); } }}
                />
              </div>
              {(b.factureerbaar_log || []).length > 0 && (
                <ol className="mt-3 space-y-2 border-t border-lijn-zacht pt-3" data-testid="factureerbaar-log">
                  {[...b.factureerbaar_log].reverse().map((e, i) => (
                    <li key={i} className="text-data text-muted-foreground">
                      {e.to ? "Aangezet" : "Uitgezet"} door {e.changed_by_name || "onbekend"} op {formatNlDate((e.changed_at || "").slice(0, 10))}
                      {e.reden ? ` — "${e.reden}"` : ""}
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Bewerken */}
      <Dialog open={editDlg} onOpenChange={setEditDlg}>
        <DialogContent className="max-w-2xl" srDescription="Pas de gegevens van deze werkbon aan.">
          <DialogHeader><DialogTitle>Werkbon bewerken</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1.5"><Label>Omschrijving (max 80)</Label><Input maxLength={80} data-testid="werkbon-edit-omschrijving" value={editForm.omschrijving} onChange={(e) => setEditForm((f) => ({ ...f, omschrijving: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Werkbeschrijving (instructie)</Label><Textarea rows={4} data-testid="werkbon-edit-werkbeschrijving" value={editForm.werkbeschrijving} onChange={(e) => setEditForm((f) => ({ ...f, werkbeschrijving: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Prioriteit</Label>
                  <Select value={editForm.prioriteit} onValueChange={(v) => setEditForm((f) => ({ ...f, prioriteit: v }))}>
                    <SelectTrigger data-testid="werkbon-edit-prioriteit"><SelectValue /></SelectTrigger>
                    <SelectContent>{["P1", "P2", "P3", "P4"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Oorzaak</Label>
                  <Select value={editForm.oorzaak_id} onValueChange={(v) => setEditForm((f) => ({ ...f, oorzaak_id: v }))}>
                    <SelectTrigger data-testid="werkbon-edit-oorzaak"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— nog geen —</SelectItem>
                      {oorzaken.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Soort</Label>
                  <Select value={editForm.soort} onValueChange={(v) => setEditForm((f) => ({ ...f, soort: v }))}>
                    <SelectTrigger data-testid="werkbon-edit-soort"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(WERKBON_SOORT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Toegewezen aan</Label>
                  <Select value={editForm.assigned_to} onValueChange={(v) => setEditForm((f) => ({ ...f, assigned_to: v }))}>
                    <SelectTrigger data-testid="werkbon-edit-assignee"><SelectValue placeholder="Niemand" /></SelectTrigger>
                    <SelectContent><SelectItem value="__none__">Niet toegewezen</SelectItem>{assignees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Geplande start</Label><Input type="date" data-testid="werkbon-edit-geplandestart" value={editForm.geplande_start} onChange={(e) => setEditForm((f) => ({ ...f, geplande_start: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>Geplande eind</Label><Input type="date" data-testid="werkbon-edit-geplandeeind" value={editForm.geplande_eind} onChange={(e) => setEditForm((f) => ({ ...f, geplande_eind: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>Geplande uren</Label><Input inputMode="decimal" placeholder="2,5" data-testid="werkbon-edit-geplandeuren" value={editForm.geplande_uren} onChange={(e) => setEditForm((f) => ({ ...f, geplande_uren: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Streefdatum (deadline)</Label><Input type="date" data-testid="werkbon-edit-streefdatum" value={editForm.streefdatum} onChange={(e) => setEditForm((f) => ({ ...f, streefdatum: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>Gereeddatum</Label><Input type="date" data-testid="werkbon-edit-gereeddatum" value={editForm.gereeddatum} onChange={(e) => setEditForm((f) => ({ ...f, gereeddatum: e.target.value }))} /></div>
              </div>
              <label className="flex items-center gap-2 text-body" data-testid="werkbon-edit-volledigedag-wrap">
                <input type="checkbox" data-testid="werkbon-edit-volledigedag" checked={editForm.volledige_dag} onChange={(e) => setEditForm((f) => ({ ...f, volledige_dag: e.target.checked }))} />
                Volledige dag (geen tijd, alleen datum)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Uitgevoerd door</Label>
                  <Select value={editForm.performed_by} onValueChange={(v) => setEditForm((f) => ({ ...f, performed_by: v }))}>
                    <SelectTrigger data-testid="werkbon-edit-performedby"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="td">TD</SelectItem><SelectItem value="extern">Extern</SelectItem></SelectContent>
                  </Select>
                </div>
                {editForm.performed_by === "extern" && (
                  <div className="space-y-1.5"><Label>Externe partij</Label><Input data-testid="werkbon-edit-externalparty" value={editForm.external_party} onChange={(e) => setEditForm((f) => ({ ...f, external_party: e.target.value }))} /></div>
                )}
              </div>
              {editForm.performed_by === "extern" && (
                <div className="space-y-1.5"><Label>Kosten extern (ex btw)</Label><MoneyInput testid="werkbon-edit-extern-bedrag" value={editForm.cost_external_ex_vat} onChange={(v) => setEditForm((f) => ({ ...f, cost_external_ex_vat: v }))} /></div>
              )}
              <div className="space-y-1.5"><Label>Notities</Label><Textarea data-testid="werkbon-edit-notes" value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditDlg(false)}>Annuleren</Button>
            <Button data-testid="werkbon-edit-save" onClick={saveEdit} disabled={busy}>{busy ? "Bezig..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Annuleren */}
      <Dialog open={cancelDlg} onOpenChange={setCancelDlg}>
        <DialogContent srDescription="Annuleer deze werkbon. De geboekte uren blijven staan en blijven zichtbaar onder Uitgezet.">
          <DialogHeader><DialogTitle>Werkbon annuleren</DialogTitle></DialogHeader>
          <p className="text-body text-muted-foreground">De geboekte uren verdwijnen niet: ze blijven op deze bon staan en blijven zichtbaar in het overzicht Uitgezet.</p>
          <div className="space-y-1.5">
            <Label>Reden {urenTotaal > 0 && <span className="text-fout-tekst">*</span>}</Label>
            <Textarea data-testid="werkbon-annuleer-reden" value={cancelReden} onChange={(e) => setCancelReden(e.target.value)} />
            {urenTotaal > 0 && (
              <p className="text-data text-muted-foreground">Op deze bon staan uren geboekt — de reden is verplicht en verschijnt onder Uitgezet.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCancelDlg(false)}>Terug</Button>
            <Button variant="destructive" data-testid="werkbon-annuleer-bevestig" onClick={cancelBon} disabled={busy || (urenTotaal > 0 && !cancelReden.trim())}>{busy ? "Bezig..." : "Annuleren"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Factureerbaar uit */}
      <Dialog open={factDlg} onOpenChange={setFactDlg}>
        <DialogContent srDescription="Zet deze werkbon op niet factureerbaar. Een reden is verplicht en wordt vastgelegd.">
          <DialogHeader><DialogTitle>Niet factureren — waarom?</DialogTitle></DialogHeader>
          <p className="text-body text-muted-foreground">Dit wordt vastgelegd met jouw naam en blijft zichtbaar in het overzicht Uitgezet.</p>
          <div className="space-y-1.5">
            <Label>Reden *</Label>
            <Textarea data-testid="werkbon-factureerbaar-reden" value={factReden} onChange={(e) => setFactReden(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setFactDlg(false)}>Terug</Button>
            <Button data-testid="werkbon-factureerbaar-bevestig" onClick={() => setFactureerbaar(false, factReden)} disabled={busy}>{busy ? "Bezig..." : "Uitzetten"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verslag */}
      <Dialog open={verslagDlg} onOpenChange={setVerslagDlg}>
        <DialogContent className="max-w-lg" srDescription="Schrijf een verslag van wat gedaan is, op deze bon, voor deze dag.">
          <DialogHeader><DialogTitle>{verslagEditId ? "Verslag aanpassen" : "Uitgevoerde werkzaamheden"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5"><Label>Datum</Label><Input type="date" data-testid="verslag-datum-input" value={verslagForm.datum} onChange={(e) => setVerslagForm((f) => ({ ...f, datum: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Commentaar</Label><Textarea rows={6} data-testid="verslag-tekst-input" value={verslagForm.tekst} onChange={(e) => setVerslagForm((f) => ({ ...f, tekst: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setVerslagDlg(false)}>Terug</Button>
            <Button data-testid="verslag-save-button" onClick={saveVerslag} disabled={busy}>{busy ? "Bezig..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Uren boeken / corrigeren */}
      <Dialog open={urenDlg} onOpenChange={setUrenDlg}>
        <DialogContent srDescription="Boek uren op deze werkbon: datum, aantal uren en het uurtype.">
          <DialogHeader><DialogTitle>{urenEditId ? "Urenregel corrigeren" : "Uren boeken"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Datum</Label><Input type="date" data-testid="uren-datum-input" value={urenForm.datum} onChange={(e) => setUrenForm((f) => ({ ...f, datum: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Uren</Label><Input inputMode="decimal" placeholder="1,5" data-testid="uren-uren-input" value={urenForm.uren} onChange={(e) => setUrenForm((f) => ({ ...f, uren: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Uurtype</Label>
                <Select value={urenForm.uurtype} onValueChange={(v) => setUrenForm((f) => ({ ...f, uurtype: v }))}>
                  <SelectTrigger data-testid="uren-uurtype-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(UURTYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!urenEditId && (
                <div className="space-y-1.5">
                  <Label>Voor</Label>
                  <Select value={urenForm.user_id} onValueChange={(v) => setUrenForm((f) => ({ ...f, user_id: v }))}>
                    <SelectTrigger data-testid="uren-voor-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{assignees.map((u) => <SelectItem key={u.id} value={u.id}>{u.id === user?.id ? `${u.name} (ik)` : u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>Notitie (optioneel)</Label><Input data-testid="uren-note-input" value={urenForm.note} onChange={(e) => setUrenForm((f) => ({ ...f, note: e.target.value }))} /></div>
          </div>
          <DialogFooter className="gap-2">
            {urenEditId && (
              <Button variant="outline" data-testid="uren-delete-button" onClick={deleteUren} disabled={busy}>Verwijderen</Button>
            )}
            <Button variant="secondary" onClick={() => setUrenDlg(false)}>Terug</Button>
            <Button data-testid="uren-save-button" onClick={saveUren} disabled={busy}>{busy ? "Bezig..." : "Boeken"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Materiaal afboeken */}
      <Dialog open={matDlg} onOpenChange={setMatDlg}>
        <DialogContent className="max-w-lg" srDescription="Boek materiaal af op deze werkbon. Elke afgifte wordt een genummerd document en verlaagt de voorraad.">
          <DialogHeader><DialogTitle>Materiaal afboeken</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {matRegels.map((r, i) => (
              <div key={i} className="grid grid-cols-[1fr_70px_90px_auto] items-end gap-2">
                <div className="space-y-1.5">
                  {i === 0 && <Label>Onderdeel</Label>}
                  <Select value={r.part_id} onValueChange={(v) => setMatRegels((rs) => rs.map((x, j) => j === i ? { ...x, part_id: v, stukprijs: x.stukprijs || amountToInput(partById[v]?.price_ex_vat) } : x))}>
                    <SelectTrigger data-testid={`afgifte-part-select-${i}`}><SelectValue placeholder="Kies onderdeel" /></SelectTrigger>
                    <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  {i === 0 && <Label>Aantal</Label>}
                  <Input inputMode="decimal" data-testid={`afgifte-aantal-input-${i}`} value={r.aantal} onChange={(e) => setMatRegels((rs) => rs.map((x, j) => j === i ? { ...x, aantal: e.target.value } : x))} />
                </div>
                <div className="space-y-1.5">
                  {i === 0 && <Label>Stukprijs</Label>}
                  <MoneyInput testid={`afgifte-stukprijs-input-${i}`} value={r.stukprijs} onChange={(v) => setMatRegels((rs) => rs.map((x, j) => j === i ? { ...x, stukprijs: v } : x))} />
                </div>
                <Button variant="ghost" size="sm" data-testid={`afgifte-regel-remove-${i}`} onClick={() => setMatRegels((rs) => rs.length > 1 ? rs.filter((_, j) => j !== i) : rs)}>×</Button>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="afgifte-regel-add" onClick={() => setMatRegels((rs) => [...rs, { part_id: "", aantal: "", stukprijs: "" }])}>
              <Plus size={16} className="mr-1.5" /> Regel toevoegen
            </Button>
            <div className="space-y-1.5"><Label>Notitie (optioneel)</Label><Input data-testid="afgifte-note-input" value={matNote} onChange={(e) => setMatNote(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setMatDlg(false)}>Terug</Button>
            <Button data-testid="afgifte-save-button" onClick={saveMateriaal} disabled={busy}>{busy ? "Bezig..." : "Boeken"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retour */}
      <Dialog open={Boolean(retourDlg)} onOpenChange={(v) => !v && setRetourDlg(null)}>
        <DialogContent className="max-w-lg" srDescription="Boek een gedeeltelijke of gehele retour op deze afgifte. Vul per regel het aantal dat terug ging.">
          <DialogHeader><DialogTitle>Retour boeken — {retourDlg?.afgifte_number}</DialogTitle></DialogHeader>
          <p className="text-data text-muted-foreground">Er kan per regel maximaal terug wat er is afgegeven minus wat al eerder is geretourneerd.</p>
          <div className="space-y-2">
            {retourForm.map((r, i) => (
              <div key={r.part_id} className="grid grid-cols-[1fr_90px] items-baseline gap-2" data-testid={`retour-regel-${i}`}>
                <div>
                  <div className="text-body text-foreground">{r.part_naam}</div>
                  <div className="text-data text-muted-foreground">Afgegeven {formatUren(r.afgegeven)} · al terug {formatUren(r.al_retour)} · rest {formatUren(r.rest)}</div>
                </div>
                <Input inputMode="decimal" placeholder="0" data-testid={`retour-aantal-input-${i}`} value={r.aantal} onChange={(e) => setRetourForm((rs) => rs.map((x, j) => j === i ? { ...x, aantal: e.target.value } : x))} disabled={r.rest <= 0} />
              </div>
            ))}
            <div className="space-y-1.5"><Label>Notitie (optioneel)</Label><Input data-testid="retour-note-input" value={retourNote} onChange={(e) => setRetourNote(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRetourDlg(null)}>Terug</Button>
            <Button data-testid="retour-save-button" onClick={saveRetour} disabled={busy}>{busy ? "Bezig..." : "Retour boeken"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Storing loskoppelen */}
      <Dialog open={unlinkDlg} onOpenChange={setUnlinkDlg}>
        <DialogContent srDescription="Loskoppelen van de storing — een verkeerde koppeling is een mis-tap, geen beslissing.">
          <DialogHeader><DialogTitle>Storing loskoppelen?</DialogTitle></DialogHeader>
          <p className="text-body text-muted-foreground">De werkbon blijft bestaan en de storing blijft bestaan; ze wijzen alleen niet meer naar elkaar.</p>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setUnlinkDlg(false)}>Terug</Button>
            <Button variant="destructive" data-testid="werkbon-unlink-fault-confirm" onClick={unlinkFault} disabled={busy}>{busy ? "Bezig..." : "Loskoppelen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
